import React, { useMemo, useState } from 'react';
import { App, Button, Drawer, Space, Table, Tag, Tooltip, Upload } from 'antd';
import {
    DownloadOutlined,
    EyeOutlined,
    FileExcelOutlined,
    ReloadOutlined,
    UploadOutlined,
} from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { usePermission } from 'components/PermissionProvider';
import { GET_ORDER_CANCEL_TASK_DETAILS, GET_ORDER_CANCEL_TASKS } from 'graphql/queries/orderCancel';
import {
    OrderCancelTask,
    OrderCancelTaskDetail,
    PagedOrderCancelTask,
    PagedOrderCancelTaskDetail,
} from 'graphql/types';
import {
    downloadOrderCancelDetails,
    downloadOrderCancelTemplate,
    PortalRestError,
    uploadOrderCancelTask,
} from 'utils/portalRestClient';

const RUNNING_STATUSES = new Set(['PENDING', 'PROCESSING']);
const FINAL_STATUSES = new Set(['ALL_SUCCESS', 'PARTIAL_SUCCESS', 'ALL_FAILED']);
const PARSING_STATUS = 'PARSING';
const VALIDATION_FAILED_STATUS = 'VALIDATION_FAILED';

const statusColor: Record<string, string> = {
    PARSING: 'processing',
    VALIDATION_FAILED: 'error',
    PENDING: 'default',
    PROCESSING: 'processing',
    ALL_SUCCESS: 'success',
    PARTIAL_SUCCESS: 'warning',
    ALL_FAILED: 'error',
    SUBMITTING: 'processing',
    SUCCESS: 'success',
    FAILED: 'error',
    IGNORED: 'default',
};

const progressColor: Record<string, string> = {
    parsing: 'processing',
    executing: 'processing',
    completed: 'success',
    validationFailed: 'error',
};

type OrderCancelTaskRow = OrderCancelTask & {
    local?: boolean;
};

const formatDate = (value?: string | null): string => {
    return value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-';
};

const formatBytes = (value?: string | null): string => {
    const size = Number(value || 0);
    if (!size) return '-';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / 1024 / 1024).toFixed(1)} MB`;
};

const getProgressKey = (task: OrderCancelTask): string => {
    if (task.status === PARSING_STATUS) return 'parsing';
    if (task.status === VALIDATION_FAILED_STATUS) return 'validationFailed';
    if (RUNNING_STATUSES.has(task.status)) return 'executing';
    if (FINAL_STATUSES.has(task.status)) return 'completed';
    return 'executing';
};

const OrderCancelPage: React.FC = () => {
    const { t } = useTranslation();
    const { message } = App.useApp();
    const { hasPermission } = usePermission();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [templateLoading, setTemplateLoading] = useState(false);
    const [localTasks, setLocalTasks] = useState<OrderCancelTaskRow[]>([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedTask, setSelectedTask] = useState<OrderCancelTask | null>(null);
    const [detailPage, setDetailPage] = useState(1);
    const [detailPageSize, setDetailPageSize] = useState(10);

    const { data, loading, refetch } = useQuery<{ portalOrderCancelTasks: PagedOrderCancelTask }>(
        GET_ORDER_CANCEL_TASKS,
        {
            variables: { page: page - 1, size: pageSize },
            pollInterval: 0,
            fetchPolicy: 'cache-and-network',
        },
    );

    const hasRunningTask = useMemo(
        () => (data?.portalOrderCancelTasks?.content || []).some((task) => RUNNING_STATUSES.has(task.status)),
        [data],
    );

    const taskRows = useMemo<OrderCancelTaskRow[]>(
        () => [...localTasks, ...(data?.portalOrderCancelTasks?.content || [])],
        [localTasks, data],
    );

    const { data: detailData, loading: detailLoading, refetch: refetchDetails } = useQuery<{
        portalOrderCancelTaskDetails: PagedOrderCancelTaskDetail;
    }>(
        GET_ORDER_CANCEL_TASK_DETAILS,
        {
            variables: { taskId: selectedTask?.id, page: detailPage - 1, size: detailPageSize },
            skip: !selectedTask,
            pollInterval: selectedTask && RUNNING_STATUSES.has(selectedTask.status) ? 5000 : 0,
            fetchPolicy: 'cache-and-network',
        },
    );

    React.useEffect(() => {
        if (!hasRunningTask) return;
        const timer = window.setInterval(() => refetch(), 5000);
        return () => window.clearInterval(timer);
    }, [hasRunningTask, refetch]);

    const renderStatus = (status: string) => (
        <Tag color={statusColor[status] || 'default'}>
            {t(`orderCancel.status.${status}`, status)}
        </Tag>
    );

    const renderProgress = (task: OrderCancelTask) => {
        const key = getProgressKey(task);
        return (
            <Tag color={progressColor[key] || 'default'}>
                {t(`orderCancel.progress.${key}`)}
            </Tag>
        );
    };

    const handleUpload = async () => {
        if (!selectedFile || uploading) return;
        const file = selectedFile;
        const localId = `local-${Date.now()}`;
        setLocalTasks((prev) => [{
            id: localId,
            sourceFileName: file.name,
            sourceFileSize: String(file.size),
            status: PARSING_STATUS,
            totalCount: 0,
            successCount: 0,
            failedCount: 0,
            ignoredCount: 0,
            createdAccountNumber: null,
            createdAt: dayjs().toISOString(),
            updatedAt: null,
            completedAt: null,
            local: true,
        }, ...prev]);
        setUploading(true);
        try {
            const result = await uploadOrderCancelTask(file);
            setLocalTasks((prev) => prev.filter((task) => task.id !== localId));
            message.success(t('orderCancel.messages.uploadSuccess'));
            setSelectedFile(null);
            await refetch();
            setSelectedTask({
                id: result.taskId,
                sourceFileName: file.name,
                sourceFileSize: String(file.size),
                status: result.status,
                totalCount: result.totalCount,
                successCount: result.successCount,
                failedCount: result.failedCount,
                ignoredCount: result.ignoredCount,
                createdAccountNumber: null,
                createdAt: null,
                updatedAt: null,
                completedAt: null,
            });
            setDetailPage(1);
        } catch (error) {
            if (error instanceof PortalRestError && error.body.code === 'VALIDATION_ERROR') {
                setLocalTasks((prev) => prev.map((task) => task.id === localId
                    ? { ...task, status: VALIDATION_FAILED_STATUS, completedAt: dayjs().toISOString() }
                    : task));
            } else {
                setLocalTasks((prev) => prev.filter((task) => task.id !== localId));
            }
            // uploadOrderCancelTask 已经根据后端响应展示了错误信息，这里只阻止开发环境 runtime overlay。
        } finally {
            setUploading(false);
        }
    };

    const handleTemplateDownload = async () => {
        if (templateLoading) return;
        setTemplateLoading(true);
        try {
            await downloadOrderCancelTemplate();
        } finally {
            setTemplateLoading(false);
        }
    };

    const handleDetailsDownload = async (task: OrderCancelTask) => {
        await downloadOrderCancelDetails(task.id);
    };

    const taskColumns = [
        {
            title: t('orderCancel.task.status'),
            dataIndex: 'status',
            key: 'status',
            width: 150,
            render: renderStatus,
        },
        {
            title: t('orderCancel.task.sourceFile'),
            dataIndex: 'sourceFileName',
            key: 'sourceFileName',
            width: 360,
            render: (value: string | null, record: OrderCancelTask) => {
                const fileName = value || '-';
                return (
                    <div className="flex min-w-0 items-center gap-2">
                        <FileExcelOutlined className="shrink-0" />
                        <Tooltip title={fileName}>
                            <span className="min-w-0 flex-1 truncate">{fileName}</span>
                        </Tooltip>
                        <span className="shrink-0 text-gray-400">{formatBytes(record.sourceFileSize)}</span>
                    </div>
                );
            },
        },
        {
            title: t('orderCancel.task.progress'),
            key: 'progress',
            width: 120,
            render: (_: unknown, record: OrderCancelTaskRow) => renderProgress(record),
        },
        {
            title: t('orderCancel.task.total'),
            dataIndex: 'totalCount',
            key: 'totalCount',
            width: 90,
        },
        {
            title: t('orderCancel.task.success'),
            dataIndex: 'successCount',
            key: 'successCount',
            width: 90,
        },
        {
            title: t('orderCancel.task.failed'),
            dataIndex: 'failedCount',
            key: 'failedCount',
            width: 90,
        },
        {
            title: t('orderCancel.task.ignored'),
            dataIndex: 'ignoredCount',
            key: 'ignoredCount',
            width: 90,
        },
        {
            title: t('orderCancel.task.createdBy'),
            dataIndex: 'createdAccountNumber',
            key: 'createdAccountNumber',
            width: 130,
            render: (value: string | null) => value || '-',
        },
        {
            title: t('orderCancel.task.createdAt'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 170,
            render: formatDate,
        },
        {
            title: t('orderCancel.task.completedAt'),
            dataIndex: 'completedAt',
            key: 'completedAt',
            width: 170,
            render: formatDate,
        },
        {
            title: t('orderCancel.task.actions'),
            key: 'actions',
            width: 160,
            render: (_: unknown, record: OrderCancelTaskRow) => record.local ? '-' : (
                <Space size={4}>
                    <Tooltip title={t('orderCancel.actions.viewDetails')}>
                        <Button
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={() => {
                                setSelectedTask(record);
                                setDetailPage(1);
                            }}
                        />
                    </Tooltip>
                    <Tooltip title={t('orderCancel.actions.downloadDetails')}>
                        <Button
                            type="text"
                            icon={<DownloadOutlined />}
                            onClick={() => handleDetailsDownload(record)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const detailColumns = [
        {
            title: t('orderCancel.detail.rowNumber'),
            dataIndex: 'rowNumber',
            key: 'rowNumber',
            width: 90,
        },
        {
            title: t('orderCancel.detail.trackingNumber'),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            width: 240,
            render: (value: string | null) => (
                <span className="block whitespace-normal break-all leading-5">
                    {value || '-'}
                </span>
            ),
        },
        {
            title: t('orderCancel.detail.status'),
            dataIndex: 'status',
            key: 'status',
            width: 130,
            render: renderStatus,
        },
        {
            title: t('orderCancel.detail.failureReason'),
            dataIndex: 'failureReason',
            key: 'failureReason',
            width: 360,
            render: (value: string | null, record: OrderCancelTaskDetail) => {
                const failureReason = record.failureMessageKey
                    ? t(record.failureMessageKey, value || '-')
                    : value || '-';
                return (
                    <span className="block whitespace-normal break-words leading-5">
                        {failureReason}
                    </span>
                );
            },
        },
        {
            title: t('orderCancel.detail.startedAt'),
            dataIndex: 'startedAt',
            key: 'startedAt',
            width: 160,
            render: formatDate,
        },
        {
            title: t('orderCancel.detail.completedAt'),
            dataIndex: 'completedAt',
            key: 'completedAt',
            width: 160,
            render: formatDate,
        },
    ];

    return (
        <div className="bg-white rounded-lg p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <Space wrap>
                    {hasPermission('portal:order-cancel:upload') && (
                        <>
                            <Upload
                                accept=".xlsx,.xls"
                                maxCount={1}
                                beforeUpload={(file) => {
                                    setSelectedFile(file);
                                    return false;
                                }}
                                onRemove={() => setSelectedFile(null)}
                                fileList={selectedFile ? [{
                                    uid: selectedFile.name,
                                    name: selectedFile.name,
                                    status: 'done',
                                }] : []}
                            >
                                <Button icon={<FileExcelOutlined />}>{t('orderCancel.actions.selectFile')}</Button>
                            </Upload>
                            <Button
                                type="primary"
                                icon={<UploadOutlined />}
                                disabled={!selectedFile}
                                loading={uploading}
                                onClick={handleUpload}
                            >
                                {t('orderCancel.actions.upload')}
                            </Button>
                        </>
                    )}
                    {hasPermission('portal:order-cancel:template') && (
                        <Button
                            icon={<DownloadOutlined />}
                            loading={templateLoading}
                            onClick={handleTemplateDownload}
                        >
                            {t('orderCancel.actions.template')}
                        </Button>
                    )}
                </Space>
                <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
                    {t('orderCancel.actions.refresh')}
                </Button>
            </div>

            <Table<OrderCancelTaskRow>
                columns={taskColumns}
                dataSource={taskRows}
                loading={loading}
                rowKey="id"
                scroll={{ x: 1650 }}
                pagination={{
                    current: page,
                    pageSize,
                    total: data?.portalOrderCancelTasks?.totalElements || 0,
                    onChange: (p, ps) => { setPage(p); setPageSize(ps); },
                    showSizeChanger: true,
                    showTotal: (total) => t('orderCancel.pagination.total', { total }),
                }}
            />

            <Drawer
                title={selectedTask ? `${t('orderCancel.detail.title')} #${selectedTask.id}` : t('orderCancel.detail.title')}
                width="min(1120px, 92vw)"
                open={!!selectedTask}
                onClose={() => setSelectedTask(null)}
                extra={<Button icon={<ReloadOutlined />} onClick={() => refetchDetails()} />}
            >
                <Table<OrderCancelTaskDetail>
                    columns={detailColumns}
                    dataSource={detailData?.portalOrderCancelTaskDetails?.content || []}
                    loading={detailLoading}
                    rowKey="id"
                    scroll={{ x: 1150 }}
                    pagination={{
                        current: detailPage,
                        pageSize: detailPageSize,
                        total: detailData?.portalOrderCancelTaskDetails?.totalElements || 0,
                        onChange: (p, ps) => { setDetailPage(p); setDetailPageSize(ps); },
                        showSizeChanger: true,
                        showTotal: (total) => t('orderCancel.pagination.total', { total }),
                    }}
                />
            </Drawer>
        </div>
    );
};

export default OrderCancelPage;
